import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { COLLECTIONS } from '../utils/constants';
import { db, storage } from './firebase';

export const uploadLibraryFile = async (file, folderPath, onProgress) => {
  if (!file) return null;

  const cloudName = 'dwuwvc1vg';
  const uploadPreset = 'smartbrain_uploads';
  const resourceType = file.type.startsWith('image/') ? 'image' : 'auto';
  const url = `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`;

  const formData = new FormData();
  formData.append('upload_preset', uploadPreset);
  formData.append('folder', folderPath);
  formData.append('file', file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress((e.loaded / e.total) * 100);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const response = JSON.parse(xhr.responseText);
        resolve(response.secure_url);
      } else {
        let errMsg = xhr.statusText;
        try {
          const errData = JSON.parse(xhr.responseText);
          errMsg = errData.error.message;
        } catch (e) {}
        reject(new Error(`Cloudinary Error: ${errMsg}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(formData);
  });
};

export const subscribeToLibraryItems = (callback) => {
  const itemsRef = collection(db, COLLECTIONS.LIBRARY_ITEMS);
  const q = query(itemsRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(items);
  });
};

export const fetchLibraryItems = async () => {
  try {
    const itemsRef = collection(db, COLLECTIONS.LIBRARY_ITEMS);
    const q = query(itemsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching library items:', error);
    throw error;
  }
};

export const addLibraryItem = async (itemData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.LIBRARY_ITEMS), {
      ...itemData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { id: docRef.id, ...itemData };
  } catch (error) {
    console.error('Error adding library item:', error);
    throw error;
  }
};

export const updateLibraryItem = async (itemId, itemData) => {
  try {
    const itemRef = doc(db, COLLECTIONS.LIBRARY_ITEMS, itemId);
    await updateDoc(itemRef, {
      ...itemData,
      updatedAt: serverTimestamp(),
    });
    return { id: itemId, ...itemData };
  } catch (error) {
    console.error('Error updating library item:', error);
    throw error;
  }
};

export const deleteLibraryItem = async (itemId, fileUrl, coverUrl) => {
  try {
    // Cloudinary files are typically managed via Admin API for deletion,
    // so we'll skip deleting from Cloudinary here to keep it simple,
    // or you could implement a serverless function to delete them securely.

    // Delete document
    await deleteDoc(doc(db, COLLECTIONS.LIBRARY_ITEMS, itemId));
    return true;
  } catch (error) {
    console.error('Error deleting library item:', error);
    throw error;
  }
};

export default {
  uploadLibraryFile,
  subscribeToLibraryItems,
  fetchLibraryItems,
  addLibraryItem,
  updateLibraryItem,
  deleteLibraryItem,
};
